// ─── Feuille : filtrer et trier le relevé d'un compte ─────────────
// Même forme que `FeuilleFiltresMois` (deux onglets, brouillon local validé
// par ✓), mais des critères de RELEVÉ : un compte n'a pas d'enveloppes à
// cocher, il a des entrées, des sorties et des mouvements internes.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, useUI, useT } from '@mop/core';
import { TRIS_OPERATIONS } from './services/filtres';

const SENS = [
  { id: 'tout',    emoji: '⇅', cle: 'finances.tousBudgets' },
  { id: 'revenu',  emoji: '↘', cle: 'finances.sensEntrees' },
  { id: 'depense', emoji: '↗', cle: 'finances.sensSorties' },
];

// `tris` et `avecHorsCalculVisible` sont des props pour que la MÊME feuille
// serve le relevé d'un compte et le plan d'une enveloppe : les critères sont
// les mêmes (sens, tri), seule la liste des tris change — trier un plan par
// « plus récente » n'aurait pas de sens, il n'y a pas de date de mouvement.
export default function FeuilleFiltresCompte({
  visible, ongletInitial = 'filtres', sens = 'tout', avecHorsCalcul = true, tri = 'recente',
  tris = TRIS_OPERATIONS, avecHorsCalculVisible = true,
  etat = 'tout', etats = null,
  onAppliquer, onFermer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [onglet, setOnglet] = useState('filtres');
  const [selSens, setSelSens] = useState('tout');
  const [selHors, setSelHors] = useState(true);
  const [selTri, setSelTri] = useState('recente');
  const [selEtat, setSelEtat] = useState('tout');

  useEffect(() => {
    if (!visible) return;
    setOnglet(ongletInitial);
    setSelSens(sens || 'tout');
    setSelHors(avecHorsCalcul !== false);
    setSelTri(tri || 'recente');
    setSelEtat(etat || 'tout');
  }, [visible, ongletInitial, sens, avecHorsCalcul, tri, etat]);

  const appliquer = () => {
    onAppliquer?.({ sens: selSens, avecHorsCalcul: selHors, tri: selTri, etat: selEtat });
    onFermer?.();
  };

  return (
    <Feuille visible={visible} titre={t('finances.filtrerTrier')} emoji="🔎" hauteur={0.7} onFermer={onFermer}
      action={{ icone: '✓', label: t('commun.valider'), onPress: appliquer }}>

      <View style={s.onglets}>
        {[{ id: 'filtres', lbl: t('finances.filtrer') }, { id: 'tri', lbl: t('finances.tri') }].map(o => (
          <TouchableOpacity key={o.id} style={[s.onglet, onglet === o.id && s.ongletOn]}
            activeOpacity={0.7} onPress={() => setOnglet(o.id)}>
            <Text style={[s.ongletTxt, onglet === o.id && s.ongletTxtOn]}>{o.lbl}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {onglet === 'filtres' ? (
        <>
          {/* Filtre par ETAT de rattachement (Cedric, 11/08) : croise le
              filtre par sens, il ne le remplace pas. */}
          {!!etats && etats.map(x => (
            <TouchableOpacity key={x.id} style={[s.item, selEtat === x.id && s.itemOn]}
              activeOpacity={0.7} onPress={() => setSelEtat(x.id)}>
              <Text style={s.coche}>{selEtat === x.id ? '◉' : '○'}</Text>
              <Text style={[s.itemTxt, selEtat === x.id && s.itemTxtOn]}>{t(x.cle)}</Text>
            </TouchableOpacity>
          ))}
          {!!etats && <View style={{ height: 10 }} />}

          {SENS.map(x => (
            <TouchableOpacity key={x.id} style={[s.item, selSens === x.id && s.itemOn]}
              activeOpacity={0.7} onPress={() => setSelSens(x.id)}>
              <Text style={s.coche}>{selSens === x.id ? '◉' : '○'}</Text>
              <Text style={{ fontSize: 17 }}>{x.emoji}</Text>
              <Text style={[s.itemTxt, selSens === x.id && s.itemTxtOn]}>{t(x.cle)}</Text>
            </TouchableOpacity>
          ))}

          {/* Les mouvements internes sont VISIBLES par défaut : les masquer
              d'office ferait disparaître de l'argent réellement parti du
              compte, et le solde affiché ne collerait plus avec la liste.
              Sans objet pour un PLAN : une prévision n'est jamais « interne ». */}
          {avecHorsCalculVisible && (
          <TouchableOpacity style={[s.item, s.itemHaut, !selHors && s.itemOn]}
            activeOpacity={0.7} onPress={() => setSelHors(v => !v)}>
            <Text style={s.coche}>{!selHors ? '☑' : '☐'}</Text>
            <Text style={{ fontSize: 17 }}>⊘</Text>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={[s.itemTxt, !selHors && s.itemTxtOn]}>{t('finances.masquerHorsCalcul')}</Text>
              <Text style={s.aide}>{t('finances.masquerHorsCalculAide')}</Text>
            </View>
          </TouchableOpacity>
          )}
        </>
      ) : (
        tris.map(x => (
          <TouchableOpacity key={x.id} style={[s.item, selTri === x.id && s.itemOn]}
            activeOpacity={0.7} onPress={() => setSelTri(x.id)}>
            <Text style={s.coche}>{selTri === x.id ? '◉' : '○'}</Text>
            <Text style={[s.itemTxt, selTri === x.id && s.itemTxtOn]}>{t(x.cle)}</Text>
          </TouchableOpacity>
        ))
      )}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  onglets: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  onglet: { flex: 1, paddingVertical: 10, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card, alignItems: 'center' },
  ongletOn: { borderColor: UI.accent, borderWidth: 1.5 },
  ongletTxt: { fontSize: 13.5, color: UI.muted, fontWeight: '700' },
  ongletTxtOn: { color: UI.accent },
  item: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12, paddingHorizontal: 12, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card, marginBottom: 8 },
  itemHaut: { marginTop: 10 },
  itemOn: { borderColor: UI.accent },
  coche: { fontSize: 16, color: UI.accent, width: 20 },
  itemTxt: { flex: 1, fontSize: 14.5, color: UI.ink },
  itemTxtOn: { fontWeight: '800' },
  aide: { fontSize: 11.5, color: UI.faint, marginTop: 2, lineHeight: 15 },
});
